import { CloseOutlined } from "@ant-design/icons"
import { Checkbox, Empty } from "antd"
import { SearchIcon } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import BaseInput from "~/components/base-input"
import { OSLoader } from "~/components/base-loader"
import { useGetMyFilesNoSession } from "~/hooks/library"

import {
  FileSource,
  getMimeFromName,
  ISelectedFile,
  MEDIA_EXTS,
  normalizeFileId
} from "./types"

interface Props {
  accept: string
  currentlyAttached?: string[]
  selected: ISelectedFile[]
  setSelected: (next: (prev: ISelectedFile[]) => ISelectedFile[]) => void
  maxFiles: number
  recentlyUploadedIds?: string[]
  showTemplateTag?: boolean
  selectableExtensions?: string[]
}

export function MyFilesTab(props: Props) {
  const {
    accept,
    currentlyAttached,
    selected,
    setSelected,
    maxFiles,
    recentlyUploadedIds,
    showTemplateTag,
    selectableExtensions
  } = props
  const recentSet = useMemo(
    () => new Set((recentlyUploadedIds ?? []).map(normalizeFileId)),
    [recentlyUploadedIds]
  )
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const { t } = useTranslation()
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(id)
  }, [search])
  const { data, isLoading } = useGetMyFilesNoSession({
    search: debouncedSearch,
    tagIds: []
  })

  const acceptList = useMemo(
    () => accept.split(",").map((s) => s.trim().toLowerCase()),
    [accept]
  )

  const files = useMemo(() => {
    const all = (data?.files ?? []) as Array<{
      id: string
      file_id: string
      name: string
      s3_path?: string
      mime_type?: string
      file_extension?: string
      transcription_status?: "pending" | "ready" | "failed"
      cdnLink?: string
      created_at?: string
    }>
    // Client-side case-insensitive name filter. The BE `/copilot/library/my-files`
    // endpoint (getMyFiles controller) ignores the body's `search` field, so
    // the search input was effectively a no-op — user types "RFP" or "rfp"
    // and the same full list comes back unchanged, which read on dev as
    // "search in attached file window is case sensitive" (Slack
    // 1779771603.590069). Applying the filter here matches the case-folding
    // BE's searchUserScopedFiles uses (`ilike`) without round-tripping.
    const term = debouncedSearch.trim().toLowerCase()
    return all
      .filter((f) => {
        const ext = (
          f.file_extension ??
          f.name?.split(".").pop() ??
          ""
        ).toLowerCase()
        return acceptList.includes("." + ext)
      })
      .filter((f) => !term || (f.name ?? "").toLowerCase().includes(term))
  }, [data, acceptList, debouncedSearch])

  const toggle = (f: (typeof files)[number]) => {
    const ext = (
      f.file_extension ??
      f.name?.split(".").pop() ??
      ""
    ).toLowerCase()
    // Normalize the picked id so the picker's dedup (and the BE-side
    // attach handler's whereIn lookup) sees the same key regardless of
    // whether the file came from the My Files list (`file_id` bare base64)
    // or the upload notify response (`id` composite). Mixing shapes
    // breaks dedup and produces duplicate chips for the same file — see
    // Slack 1779771769.259519.
    const normalizedFileId = normalizeFileId(f.file_id ?? f.id)
    const sf: ISelectedFile = {
      source: "my_files" as FileSource,
      file_id: normalizedFileId,
      s3_path: f.s3_path ?? "",
      file_name: f.name,
      mime_type: f.mime_type || getMimeFromName(f.name),
      is_media: MEDIA_EXTS.has(ext),
      transcription_status: f.transcription_status
    }
    setSelected((prev) => {
      if (prev.find((p) => normalizeFileId(p.file_id) === normalizedFileId)) {
        return prev.filter(
          (p) => normalizeFileId(p.file_id) !== normalizedFileId
        )
      }
      if (prev.length >= maxFiles) return prev
      return [...prev, sf]
    })
  }

  return (
    <div className="flex flex-col gap-2">
      <BaseInput
        placeholder="Search files…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="!h-10 [&_input]:!h-10 [&_input]:!text-left [&_input]:!pt-3 [&_input]:!pb-3 [&_input]:!box-border [&_input]:!leading-normal [&.ant-input-affix-wrapper]:!flex [&.ant-input-affix-wrapper]:!items-center"
        allowClear={{
          clearIcon: (
            <CloseOutlined className="w-4 h-4 dark:text-dark-6 text-dark-2" />
          )
        }}
        onClear={() => setSearch("")}
        suffix={
          <SearchIcon
            className="w-4 h-4 dark:text-dark-6 text-dark-2"
            strokeWidth={1}
          />
        }
      />
      {isLoading ? (
        <div className="w-full min-h-[200px] flex items-center justify-center border border-light-border dark:border-dark-border rounded-lg bg-white dark:bg-dark-1">
          <OSLoader />
        </div>
      ) : files.length === 0 ? (
        <div className="w-full min-h-[200px] flex items-center justify-center border border-light-border dark:border-dark-border rounded-lg bg-white dark:bg-dark-1">
          <Empty
            description={<p className="text-xs">No files yet.</p>}
            className="mt-0"
          />
        </div>
      ) : (
        <ul className="max-h-96 overflow-auto border border-light-border dark:border-dark-border rounded-lg bg-white dark:bg-dark-1">
          {files.map((f) => {
            // Compare with normalize on both sides so the checkbox reflects
            // already-selected state even when an entry was added with a
            // different id shape (e.g. composite from UploadPane vs bare
            // from this tab's list).
            const fNorm = normalizeFileId(f.file_id ?? f.id)
            const checked = !!selected.find(
              (p) => normalizeFileId(p.file_id) === fNorm
            )
            const disabledByCap = !checked && selected.length >= maxFiles
            const disabledByAttached = currentlyAttached?.includes(f.file_id)
            // Show ALL files, but only the selectable types (e.g. docx/xlsx
            // for fill-template) can be picked — others render disabled.
            const rowExt = (
              f.file_extension ??
              f.name?.split(".").pop() ??
              ""
            ).toLowerCase()
            const disabledByType =
              !!selectableExtensions &&
              selectableExtensions.length > 0 &&
              !selectableExtensions.includes(rowExt)
            const createdAtMs = f.created_at
              ? new Date(f.created_at).getTime()
              : NaN
            const within24h =
              !Number.isNaN(createdAtMs) &&
              Date.now() - createdAtMs < 24 * 60 * 60 * 1000
            const isRecent = within24h || recentSet.has(fNorm)
            return (
              <li
                key={f.file_id}
                style={
                  isRecent
                    ? { backgroundColor: "rgba(225, 190, 231, 0.15)" }
                    : undefined
                }
                className={
                  "flex items-center justify-between gap-4 py-2 px-2.5 border-b-[0.5px] border-light-border dark:border-dark-border last:border-b-0 " +
                  (disabledByType
                    ? "cursor-not-allowed opacity-50 "
                    : "cursor-pointer ") +
                  (isRecent || disabledByType
                    ? ""
                    : "hover:bg-gray-50 dark:hover:bg-dark-2")
                }
                onClick={() =>
                  !disabledByCap &&
                  !disabledByAttached &&
                  !disabledByType &&
                  toggle(f)
                }
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Checkbox
                    checked={checked}
                    disabled={
                      disabledByCap || !!disabledByAttached || disabledByType
                    }
                    onChange={() => toggle(f)}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-shrink-0"
                  />
                  <p className="text-xs dark:text-dark-6 text-dark-2 truncate">
                    {f.name}
                  </p>
                </div>
                {showTemplateTag && checked ? (
                  <span className="text-[11px] font-semibold tracking-wide uppercase text-primary dark:text-primary-light shrink-0">
                    {t("ask_os.label_template_tag")}
                  </span>
                ) : null}
                {isRecent ? (
                  <span className="text-[11px] italic text-gray-500 dark:text-gray-400 shrink-0">
                    Recently added
                  </span>
                ) : null}
                {f.transcription_status === "pending" ? (
                  <span className="text-xs text-amber-600 shrink-0">
                    Transcribing…
                  </span>
                ) : null}
                {f.transcription_status === "failed" ? (
                  <span className="text-xs text-red-600 shrink-0">
                    Transcription failed
                  </span>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
